import { FeatureFlag } from '@ps/web-core-enums';
import debounce from 'debounce-promise';
import type { FieldValidator } from 'final-form';
import { memoize } from 'lodash';
import React, { useRef } from 'react';
import { useIntl } from 'react-intl';

import type { RequestError } from '@swf/core/errors/RequestError';
import useFeature from '@swf/core/hooks/useFeature';
import useRequest from '@swf/core/hooks/useRequest';
import type { TrackedTranslation, TranslationTag } from '@swf/core/types/i18n';
import type { Error } from '@swf/core/utils/validators';
import { WHITE_START_OR_END_REGEXP } from '@swf/core/utils/validators';

import type { BonusInfo } from '../../../../types/Cashier';
import { checkBonusData } from '../../../../utils/bonus';
import TextInput from '../../../TextInput/TextInput';

import {
  TXTSRV_Please_enter_a_valid_bonus_cod_ELL,
  TXTWCL_Bonus_code,
  TXTWCL_Your_code,
  TXTWCL_Your_code_cannot_start_or_end__ELL
} from './BonusInput.messages.json';

interface Props {
  selected?: boolean;
  label?: TrackedTranslation;
  placeholder?: TranslationTag;
  className?: string;
  checkBonusResponse?: boolean;
}

const fieldName = 'deposit.bonusCode';

const errorCodes = {
  TXTWCL_The_Bonus_Code_entered_is_not__ELL: TXTSRV_Please_enter_a_valid_bonus_cod_ELL
};

const BonusInput: React.FC<Props> = ({
  selected,
  label = TXTWCL_Bonus_code,
  placeholder = TXTWCL_Your_code,
  className,
  checkBonusResponse
}) => {
  const intl = useIntl();
  const request = useRequest();
  const bonusResponses = useRef<{ [k: string]: BonusInfo }>();
  const isAutofocusEnabled = useFeature(FeatureFlag.SwcSignupRmfAutofocus);

  const validateBonusCode: FieldValidator<string> = memoize(
    (bonusCode, allValues) => {
      const checkBonusCodeAsync = async (): Promise<Error> => {
        if (!bonusCode || !selected) {
          return { valid: true };
        }

        if (WHITE_START_OR_END_REGEXP.test(bonusCode)) {
          return {
            valid: false,
            message: [
              TXTSRV_Please_enter_a_valid_bonus_cod_ELL,
              TXTWCL_Your_code_cannot_start_or_end__ELL
            ]
          };
        }

        try {
          if (checkBonusResponse && bonusResponses.current && bonusResponses.current[bonusCode]) {
            return checkBonusData(bonusResponses.current[bonusCode], allValues);
          }

          const response = await request<BonusInfo>({
            service: 'wc3.bonus.getCodeInfo',
            body: { bonusCode }
          }).toPromise();

          if (checkBonusResponse) {
            bonusResponses.current = { ...bonusResponses.current, [bonusCode]: response.body };
            return checkBonusData(response.body, allValues);
          }
          return { valid: true };
        } catch (e) {
          const error = e as RequestError<{ fieldErrors?: { code: string }[] }>;
          const code = error?.body?.fieldErrors?.[0].code;
          return { valid: false, message: errorCodes[code as string] };
        }
      };
      return debounce(checkBonusCodeAsync, 500)();
    },
    checkBonusResponse
      ? (bonusCode, allValues) => JSON.stringify([bonusCode, allValues])
      : undefined
  );

  return (
    <TextInput
      id="ownBonusCode"
      name={fieldName}
      label={label}
      autoComplete="off"
      placeholder={intl.formatMessage(placeholder)}
      validators={[validateBonusCode]}
      className={className}
      autoFocus={isAutofocusEnabled}
    />
  );
};

export default BonusInput;
